import type { UnifiedProductSummary } from '@noths/polaris-client-unified-product-service';
import {
  toProductCardBadge,
  toProductCardRating,
} from '@noths/polaris-design-system-candidate-components';

import { transformUnifiedProductToProduct } from 'src/utils/unifiedProductToProduct';
import type { PartnerProduct } from './types';

/**
 * One of a partner's products is one card's props. The mapping from unified product
 * to OOUX product is shared with the product carousel, so both agree on what a
 * product means; only the card-level presentation is decided here.
 */
export const transformUnifiedProductToPartnerProduct = (
  product: UnifiedProductSummary,
  {
    priority,
    promoteAsSale,
    showMultipleImages,
  }: { priority: boolean; promoteAsSale: boolean; showMultipleImages: boolean },
): PartnerProduct => {
  /* `suppressPartnerBadges` is deliberately not set, where a PLP partner page does set it.
     There every card is the same partner, so Top Brand says nothing a customer can act on;
     here the block renders several partners side by side and the badge tells them apart. */
  const badge = toProductCardBadge(product.attributes);
  const rating = toProductCardRating(product.ratings?.averageRating);

  return {
    ...(badge && { badge }),
    isNew: product.isNew,
    priority,
    product: transformUnifiedProductToProduct(product, { showMultipleImages }),
    /* From the site-wide Contentstack switch, not `product.onSale`. Nothing UPS carries
       distinguishes a sale from an offer, so presenting a discount as either is a
       decision about the whole site rather than about this block or this product. */
    promoteAsSale,
    ...(rating !== undefined && { rating }),
  };
};
